import React, { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { getDeviceImageOrNone } from 'utils/Devices';
import { getDefaultCuff } from 'utils/RecommendCuffSku';

import { CuffSizeSelector } from './CuffSizeSelector';
import { DeviceCard } from './DeviceCard';
import { DeviceFilterBar } from './DeviceFilterBar';
import { DeviceListEmptyState } from './DeviceListEmptyState';
import { SeriesTierLegend } from './SeriesTierLegend';

interface DeviceListWithFiltersProps {
  devices: IDeviceOrderingCatalogItem[];
  selectedSkus: string[];
  onToggle: (sku: string) => void;
  groupBy?: (device: IDisplayableDevice) => string;
  renderCardFooter?: (device: IDisplayableDevice) => React.ReactNode;
  selectionsOptions?: Record<string, string>;
  onOptionSelect?: (deviceSku: string, optionSku: string) => void;
  recommendation?: Record<string, string>;
  maxSelectionsPerGroup?: number;
  showOptions?: boolean;
  /**
   * What this order contains, unlike `selectedSkus`, which also carries what the patient owns
   * already. Omitted, every option is offered.
   */
  orderedSkus?: string[];
  patientDevices?: IDeviceListItem[];
  renderCuffNotice?: (device: IDisplayableDevice) => React.ReactNode;
}

interface IDisplayableDevice {
  sku: string;
  name: string;
  manufacturer: string;
  measurementType: string;
  series: DeviceSeries;
  imageUrl?: string;
  shippingProvider: string;
  lineOrder: number;
  deviceOrderingId?: string;
  extendedRange?: boolean;
  options?: IDeviceOrderingOption[];
  parentDeviceOrderingId?: string | null;
}

const defaultGroupBy = (device: IDisplayableDevice) => device.measurementType;

export const DeviceSelectionList = ({
  devices,
  selectedSkus,
  onToggle,
  groupBy = defaultGroupBy,
  renderCardFooter,
  selectionsOptions,
  onOptionSelect,
  recommendation,
  maxSelectionsPerGroup,
  showOptions = true,
  orderedSkus,
  patientDevices,
  renderCuffNotice
}: DeviceListWithFiltersProps) => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [localOptionsSelections, setLocalOptionsSelections] = useState<Record<string, string>>({});

  const effectiveOptionSelections = selectionsOptions ?? localOptionsSelections;

  const handleOptionSelect = (deviceSku: string, optionSku: string) => {
    if (!selectionsOptions) {
      setLocalOptionsSelections((prev) => ({ ...prev, [deviceSku]: optionSku }));
    }
    onOptionSelect?.(deviceSku, optionSku);
  };

  const displayableDevices = useMemo(
    () =>
      devices.map(
        (device) =>
          ({
            sku: device.sku,
            name: intl.formatMessage({
              id: device.displayName,
              defaultMessage: device.displayName
            }),
            manufacturer: device.manufacturer,
            series: device.series,
            measurementType: device.displayType,
            imageUrl: getDeviceImageOrNone(device.displayName),
            shippingProvider: device.shippingProvider ?? '',
            extendedRange: device.extendedRange,
            lineOrder: device.lineOrder,
            options: device.options,
            parentDeviceOrderingId: device.parentDeviceOrderingId
          }) as IDisplayableDevice
      ),
    [devices, intl]
  );

  const groupOrder = useMemo(() => {
    const order = new Map<string, number>();
    displayableDevices.forEach((device) => {
      const key = groupBy(device);
      const current = order.get(key);
      if (current === undefined || device.lineOrder < current) {
        order.set(key, device.lineOrder);
      }
    });
    return order;
  }, [displayableDevices, groupBy]);

  const sortGroupKey = (a: string, b: string): number =>
    (groupOrder.get(a) ?? 0) - (groupOrder.get(b) ?? 0);

  const categories = useMemo(
    () =>
      [...groupOrder.keys()].sort((a, b) => (groupOrder.get(a) ?? 0) - (groupOrder.get(b) ?? 0)),
    [groupOrder]
  );

  const filtered = useMemo(() => {
    const searchLower = search.toLowerCase();
    return displayableDevices.filter((device) => {
      const matchesSearch =
        !searchLower ||
        device.name.toLowerCase().includes(searchLower) ||
        device.manufacturer.toLowerCase().includes(searchLower) ||
        device.sku.toLowerCase().includes(searchLower);
      const matchesCategory = selectedCategory === 'all' || groupBy(device) === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [displayableDevices, search, selectedCategory, groupBy]);

  const grouped = useMemo(() => {
    const byGroup = filtered.reduce((acc, device) => {
      const key = groupBy(device);
      if (!acc.has(key)) acc.set(key, []);
      acc.get(key)?.push(device);
      return acc;
    }, new Map<string, IDisplayableDevice[]>());

    return Array.from(byGroup).reduce(
      (acc: Record<string, IDisplayableDevice[]>, [groupKey, groupDevices]) => {
        acc[groupKey] = [...groupDevices].sort(
          (first, second) => first.lineOrder - second.lineOrder
        );
        return acc;
      },
      {}
    );
  }, [filtered, groupBy]);

  const selectedGroups = useMemo(() => {
    if (!maxSelectionsPerGroup) return new Set<string>();
    const groups = new Set(
      Object.entries(grouped)
        .filter(
          ([, groupDevices]) =>
            groupDevices.filter((d) => selectedSkus.includes(d.sku)).length >= maxSelectionsPerGroup
        )
        .map(([group]) => group)
    );
    // A cuff left assigned without its monitor must not block ordering a new monitor.
    patientDevices?.forEach((item) => {
      if (item.deviceOrdering && !item.deviceOrdering.parent) {
        groups.add(item.deviceOrdering.displayType);
      }
    });
    return groups;
  }, [grouped, selectedSkus, maxSelectionsPerGroup, patientDevices]);

  return (
    <div>
      <SeriesTierLegend />

      <DeviceFilterBar
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        search={search}
        onSearchChange={setSearch}
      />

      {filtered.length === 0 ? (
        <DeviceListEmptyState
          onClearFilters={() => {
            setSearch('');
            setSelectedCategory('all');
          }}
        />
      ) : (
        Object.entries(grouped)
          .sort(([a], [b]) => sortGroupKey(a, b))
          .map(([groupKey, groupDevices]) => (
            <div key={groupKey} className="mb-4">
              <p className="text-uppercase mb-2 small fw-semibold text-secondary device-group-label">
                {intl.formatMessage({ id: groupKey })} ({groupDevices.length})
              </p>
              <div className="d-flex flex-column gap-2">
                {groupDevices.map((device) => {
                  const isSelected = selectedSkus.includes(device.sku);
                  // A default accessory has no price of its own and is only sold with its parent.
                  const parentIsInThisOrder = orderedSkus ? orderedSkus.includes(device.sku) : true;
                  const cuffOptions = showOptions
                    ? device.options?.filter(
                        (o) => Boolean(o.details) && (parentIsInThisOrder || !o.isDefaultAccessory)
                      ) ?? []
                    : [];

                  const recommendedOptionSku = recommendation?.[device.sku];
                  const expandedContent =
                    isSelected && cuffOptions.length > 0 ? (
                      <CuffSizeSelector
                        options={cuffOptions}
                        selectedSku={
                          effectiveOptionSelections[device.sku] ??
                          recommendedOptionSku ??
                          getDefaultCuff(cuffOptions)?.sku ??
                          cuffOptions[0].sku
                        }
                        onSelect={(sku) => handleOptionSelect(device.sku, sku)}
                        recommendedSku={recommendedOptionSku}
                        notice={renderCuffNotice?.(device)}
                      />
                    ) : null;

                  return (
                    <DeviceCard
                      key={device.sku}
                      device={device}
                      isSelected={isSelected}
                      disabled={!isSelected && selectedGroups.has(groupBy(device))}
                      onToggle={() => onToggle(device.sku)}
                      footer={renderCardFooter?.(device)}
                      expandedContent={expandedContent ?? undefined}
                    />
                  );
                })}
              </div>
            </div>
          ))
      )}
    </div>
  );
};
